import BlogPost from 'components/blog/blog-post/blog-post';
import Container from 'components/container/container';
import Link from 'next/link';
import { LIBUM_URL } from 'shared/constants/globals';
import { BLOG_ITEMS } from 'shared/data/blogs';

const BLOG = BLOG_ITEMS.find((b) => b.id === 'cu-building-the-future')!;

export default function CuBuildingTheFuture() {
  const title = `${BLOG.title} | Libum`;
  const description = BLOG.description;
  const keywords = BLOG.keywords.join(',');
  const url = `${LIBUM_URL}/blog/${BLOG.id}`;

  return (
    <Container title={title} description={description} keywords={keywords} url={url} socialCard="blog">
      <div style={{ width: '100%' }}>
        <BlogPost blog={BLOG}>
          <p>
            Last week, we had the honor of attending this year&apos;s{' '}
            <a href="https://www.cubuild.org/" target="_blank" rel="noreferrer">
              CU Build
            </a>{' '}
            and wow was it an amazing experience! For those that don&apos;t know, CU Build is a 3-day build-a-thon where
            credit union innovators from all over the country come together to design and develop creative solutions.
            This year&apos;s theme was <b>&quot;Show Me the Data&quot;</b> and let us tell you, the teams did not
            disappoint!
          </p>
          <p>
            From the moment that we walked in and witnessed the literal &quot;snack mountain&quot; a few feet away from
            our table, we knew that this was going to be a great time. On top of that, our neighbors,{' '}
            <a href="https://www.cutek.com/" target="_blank" rel="noreferrer">
              CUTEK
            </a>{' '}
            (one of the very generous sponsors & curator of snack mountain) assured us there was going to be no shortage
            of snacks throughout the event{' '}
            <img src="/icons/twitter/party-popper.png" alt="Party popper emoji" className="icon" />
          </p>
          <figure>
            <img
              src="/images/525aa648-29ca-4714-ad9d-6c32e965aa9d.png"
              alt="Table full with of snacks for the builders at CU Build"
            />
            <figcaption>Snack Mountain, circa 2023</figcaption>
          </figure>
          <div className="divider" />
          <h2 className="anchor" id="cu-build-recap">
            <Link href="#cu-build-recap">CU Build Recap</Link>
          </h2>
          <p>
            The CU Build event consisted of nine teams (<b>CU Lambs</b>, <b>Digital Landscapers</b>, <b>EZ Drivers</b>,{' '}
            <b>Control Characters</b>, <b>Mind Craft</b>, <b>Warning 21</b>, <b>Team Newbies</b>, <b>camelCase</b>, and{' '}
            <b>Dungeons and Paper</b>) each given complete autonomy to forge their very own creative solution to
            &quot;show the judges the data&quot;. Teams were encouraged to leverage the latest tech provided by various
            integrating partners with names like{' '}
            <a href="https://jackhenry.dev/" target="_blank" rel="noreferrer">
              Jack Henry
            </a>
            ,{' '}
            <a href="https://finofr.com/" target="_blank" rel="noreferrer">
              FINOFER
            </a>
            , and{' '}
            <a href="https://www.q2developer.com/" target="_blank" rel="noreferrer">
              Q2
            </a>{' '}
            to name a few.
          </p>
          <p>
            Knowing the likely dependency on SymXchange when developing applications that communicate with the Symitar
            core, we felt it&apos;d be the perfect opportunity to launch an &quot;early access&quot; release of{' '}
            <Link href="/nexum" target="_blank">
              NexumAPI
            </Link>{' '}
            for the CU Build teams <img src="/icons/twitter/fire.png" alt="Fire emoji" className="icon" /> Little did we
            know that <b>eight</b> of the nine teams would end up using NexumAPI over vanilla SymXchange generating over{' '}
            <b>6,000</b> requests in just 36 hours! It was definitely a great{' '}
            <a href="https://en.wikipedia.org/wiki/Quality_assurance" target="_blank" rel="noreferrer">
              QA
            </a>{' '}
            test for us but we&apos;ll chat about that experience in a future blog post!
          </p>
          <figure>
            <img
              src="/images/6f0922f3-0afc-45e7-9d81-d02d2e0690cb.jpeg"
              alt="Don Nguyen collaborating with a builder at CU Build"
            />
            <figcaption>
              Collaboration between Libum Founder, Don Nguyen, and CU Builder. Photo by{' '}
              <a href="https://www.jackhenry.com/" target="_blank" rel="noreferrer">
                Jack Henry
              </a>
            </figcaption>
          </figure>
          <p>
            One of our biggest benefits from participating as an integrating partner was getting to be a part of the
            building experience - the atmosphere of constant creativity and collaboration was something we hadn&apos;t
            been a part of specifically for the Symitar community. Builders from various teams would challenge the norm
            and if they hit a wall, reach out to us and other partners alike for a push in the right direction.
          </p>
          <div className="divider" />
          <h2 className="anchor" id="and-the-winner-is">
            <Link href="#and-the-winner-is">And the Winner Is...</Link>
          </h2>
          <p>
            Of course, you can&apos;t just showcase some tech in a &quot;build-a-thon&quot; and NOT give away some
            prizes. That&apos;s why we decided to give away <b>$50 VISA gift cards</b>,{' '}
            <b>
              custom Libum laser-engraved{' '}
              <a href="https://www.swell.com/" target="_blank" rel="noreferrer">
                S&apos;well
              </a>{' '}
              water bottles
            </b>
            , and <b>early adopter invitations</b> to each of the builders on the winning team for the{' '}
            <i>Best Use of NexumAPI</i>.
          </p>
          <p>
            While the judging was tough since every team gave such incredible effort and unique implementations,
            ultimately the
            <img src="/icons/twitter/trophy.png" alt="Trophy emoji" className="icon" /> went to <b>camelCase</b> (
            <Link href="https://www.linkedin.com/in/patrick-berney-376884126/" target="_blank">
              Patrick Berney
            </Link>{' '}
            -{' '}
            <Link href="https://www.linkedin.com/company/indiana-members-credit-union/" target="_blank">
              Indiana Members CU
            </Link>
            ,{' '}
            <Link href="https://www.linkedin.com/in/maxpool/" target="_blank">
              Max Pool
            </Link>{' '}
            -{' '}
            <Link href="https://www.linkedin.com/company/sycorr/about/" target="_blank">
              Sycorr
            </Link>
            ,{' '}
            <Link href="https://www.linkedin.com/in/adam-j-steiner/" target="_blank">
              Adam Steiner
            </Link>{' '}
            -{' '}
            <Link href="https://www.linkedin.com/company/fffcuofmd/" target="_blank">
              First Financial FCU
            </Link>
            ,{' '}
            <Link href="https://www.linkedin.com/in/brian-chambers-b017a497/" target="_blank">
              Brian Chambers
            </Link>{' '}
            -{' '}
            <Link href="https://www.linkedin.com/company/aacreditunion/" target="_blank">
              American Airlines FCU
            </Link>
            , Hannah Stone -{' '}
            <Link href="https://www.highmarkfcu.com/" target="_blank">
              Highmark FCU
            </Link>
            , and Lakshman Prabu -{' '}
            <Link href="https://www.linkedin.com/company/tyfone/" target="_blank">
              Tyfone
            </Link>
            ) for their brilliant approach to leverage the{' '}
            <b>
              <i>executePowerOn</i>
            </b>{' '}
            operation on a frequency to pull in large subsets of Symitar data, merge them back together, and feed it
            into a{' '}
            <a href="https://en.wikipedia.org/wiki/Machine_learning" target="_blank" rel="noreferrer">
              ML
            </a>{' '}
            model. They also chose to integrate a NexumAPI call from{' '}
            <a href="https://kore.ai/" target="_blank" rel="noreferrer">
              Kore.ai
            </a>{' '}
            which prompted us to make a few &quot;on the spot&quot; server side changes for more robust integratability
            with other vendors.
          </p>
          <figure>
            <img
              src="/images/a3115062-c8f6-442b-9614-c19862c6de4d.jpg"
              alt="Libum founders with winners of best in use of NexumAPI at CU Build"
            />
            <figcaption>
              Libum co-founders (left) with the winners of <i>Best Use of NexumAPI</i>, camelCase.
              <br />
              Photo by{' '}
              <a href="https://www.jackhenry.com/" target="_blank" rel="noreferrer">
                Jack Henry
              </a>
            </figcaption>
          </figure>
          <p>
            As we sit back and reflect on our time at CU Build, we can&apos;t help but feel a sense of excitement for
            the future of credit union technology, specifically for the Symitar community given the talent & creativity
            demonstrated throughout the event. It&apos;s honestly so impressive what the builders were able to come up
            with in only 36 hours collaborating with team members they hadn&apos;t met prior to the event.
          </p>
          <div className="divider" />
          <p>
            If you&apos;re a credit union innovator, or even an integrating partner and looking to get your tech into
            some of the creative minds in the Symitar community, come to CU Build next year! We&apos;ll see you there{' '}
            <img src="/icons/twitter/rocket.png" alt="Rocket emoji" className="icon" />
          </p>
        </BlogPost>
      </div>
    </Container>
  );
}
